import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Check as CheckIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import axios from 'axios';
import SetupLayout from './SetupLayout';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const countryCodes = [
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+1', country: 'US', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
  { code: '+65', country: 'SG', flag: '🇸🇬' },
];

const OrganizationSignup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    organizationName: '',
    adminEmail: '',
    countryCode: '+91',
    phoneNumber: '',
    password: '',
    agreeTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const signupData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
  const selectedPlan = signupData.plan || 'mail-lite';

  useEffect(() => {
    const storedData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
    if (!storedData.plan) {
      navigate('/signup');
    }
  }, [navigate]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
    setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.organizationName.trim()) {
      setError('Organization name is required');
      return false;
    }
    if (!formData.adminEmail.trim()) {
      setError('Email address is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      setError('Mobile number is required');
      return false;
    }
    if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, ''))) {
      setError('Please enter a valid 10-digit mobile number');
      return false;
    }
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      setError('Password must contain uppercase, lowercase, and number');
      return false;
    }
    if (!formData.agreeTerms) {
      setError('You must agree to the terms and conditions');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/signup/register`, {
        organizationName: formData.organizationName,
        adminEmail: formData.adminEmail,
        countryCode: formData.countryCode,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        plan: selectedPlan,
      });

      if (response.data.success || response.data.data?.success) {
        const data = response.data.data || response.data;
        sessionStorage.setItem('signupData', JSON.stringify({
          ...signupData,
          organizationId: data.data?.organizationId || data.organizationId,
          phone: `${formData.countryCode}${formData.phoneNumber}`,
          email: formData.adminEmail,
          token: data.data?.token || data.token,
          organization: formData.organizationName,
          password: formData.password,
        }));
        navigate('/setup/verify');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPlanDetails = () => {
    const plans: Record<string, { name: string; storage: string; price: string; features: string[] }> = {
      'free': { name: 'Free', storage: '1 GB', price: '₹0', features: ['1 GB storage', 'Up to 3 users', 'Webmail access', 'Basic support'] },
      'mail-lite': { name: 'Mail Lite', storage: '5 GB', price: '₹59', features: ['5 GB storage', 'Unlimited users', 'POP/IMAP access', 'Spam protection'] },
      'mail-premium': { name: 'Mail Premium', storage: '50 GB', price: '₹99', features: ['50 GB storage', 'Email backup', 'Priority delivery', '24/7 support'] },
      'business': { name: 'Business', storage: '100 GB', price: '₹199', features: ['100 GB storage', 'Admin console', 'Activity logs', 'API access'] },
      'enterprise': { name: 'Enterprise', storage: '500 GB', price: '₹399', features: ['500 GB storage', 'SSO & DLP', 'Dedicated IP', '99.9% SLA'] },
    };
    return plans[selectedPlan] || plans['mail-lite'];
  };

  const planDetails = getPlanDetails();

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: '#f9fafb',
      fontFamily: 'Inter, sans-serif',
      '& fieldset': { borderColor: '#e5e7eb' },
      '&:hover fieldset': { borderColor: '#dc2626' },
      '&.Mui-focused fieldset': { borderColor: '#dc2626' },
    },
    '& .MuiOutlinedInput-input': { py: 1.5, fontFamily: 'Inter, sans-serif' },
  };

  return (
    <SetupLayout activeStep={1} backPath="/signup">
      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', py: 4 }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, width: '100%', display: 'flex', gap: 6, flexDirection: { xs: 'column', md: 'row' } }}>
          
          {/* Left Side - Form */}
          <Box sx={{ flex: 1, maxWidth: { md: 500 } }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 1, fontFamily: 'Inter, sans-serif' }}>
              Create your account
            </Typography>
            <Typography sx={{ color: '#6b7280', mb: 4, fontFamily: 'Inter, sans-serif' }}>
              Get started with your business email in minutes
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
              {/* Organization Name */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#374151', mb: 1, fontWeight: 500, fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                  Organization / Company Name
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your company name"
                  value={formData.organizationName}
                  onChange={handleChange('organizationName')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
                  sx={textFieldSx}
                />
              </Box>

              {/* Email Address */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#374151', mb: 1, fontWeight: 500, fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="Enter your email address"
                  value={formData.adminEmail}
                  onChange={handleChange('adminEmail')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
                  sx={textFieldSx}
                />
                <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem', mt: 0.5, fontFamily: 'Inter, sans-serif' }}>
                  We'll send a verification code to this email
                </Typography>
              </Box>

              {/* Mobile Number */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#374151', mb: 1, fontWeight: 500, fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                  Mobile Number
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Select
                    value={formData.countryCode}
                    onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                    sx={{ width: 100, borderRadius: 2, bgcolor: '#f9fafb', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } }}
                  >
                    {countryCodes.map((c) => (
                      <MenuItem key={c.code} value={c.code}>{c.flag} {c.code}</MenuItem>
                    ))}
                  </Select>
                  <TextField
                    fullWidth
                    placeholder="Enter your mobile number"
                    value={formData.phoneNumber}
                    onChange={handleChange('phoneNumber')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
                    sx={textFieldSx}
                  />
                </Box>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem', mt: 0.5, fontFamily: 'Inter, sans-serif' }}>
                  We'll send a verification OTP to this number and your email
                </Typography>
              </Box>

              {/* Password */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#374151', mb: 1, fontWeight: 500, fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                  Password
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={handleChange('password')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#9ca3af' }} /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff sx={{ color: '#9ca3af' }} /> : <Visibility sx={{ color: '#9ca3af' }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldSx}
                />
                <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem', mt: 0.5, fontFamily: 'Inter, sans-serif' }}>
                  Min 8 characters with uppercase, lowercase & number
                </Typography>
              </Box>

              {/* Terms */}
              <FormControlLabel
                control={<Checkbox checked={formData.agreeTerms} onChange={handleChange('agreeTerms')} sx={{ color: '#dc2626', '&.Mui-checked': { color: '#dc2626' } }} />}
                label={
                  <Typography sx={{ fontSize: '0.875rem', color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
                    I agree to the <Link href="#" sx={{ color: '#dc2626' }}>Terms of Service</Link> and <Link href="#" sx={{ color: '#dc2626' }}>Privacy Policy</Link>
                  </Typography>
                }
                sx={{ mb: 3 }}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  py: 1.5,
                  bgcolor: '#dc2626',
                  '&:hover': { bgcolor: '#b91c1c' },
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  borderRadius: 2,
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Get OTP'}
              </Button>

              <Typography sx={{ textAlign: 'center', mt: 3, color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
                Already have an account?{' '}
                <Link component={RouterLink} to="/login" sx={{ color: '#dc2626', fontWeight: 600 }}>Sign In</Link>
              </Typography>
            </form>
          </Box>

          {/* Right Side - Plan Details */}
          <Box sx={{ flex: 1, maxWidth: { md: 400 } }}>
            <Box sx={{ bgcolor: '#f8fafc', borderRadius: 3, p: 4, border: '1px solid #e5e7eb', position: 'sticky', top: 24 }}>
              <Typography sx={{ fontWeight: 600, color: '#111827', mb: 3, fontSize: '1.125rem', fontFamily: 'Inter, sans-serif' }}>
                Selected Plan
              </Typography>
              
              <Box sx={{ border: '2px solid #dc2626', borderRadius: 2, p: 3, bgcolor: '#ffffff', mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '1.125rem', fontFamily: 'Inter, sans-serif' }}>
                    {planDetails.name}
                  </Typography>
                  <Button onClick={() => navigate('/signup')} sx={{ color: '#dc2626', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                    Change
                  </Button>
                </Box>
                <Typography sx={{ color: '#dc2626', fontWeight: 700, fontSize: '1.5rem', fontFamily: 'Inter, sans-serif' }}>
                  {planDetails.price}<Typography component="span" sx={{ color: '#6b7280', fontWeight: 400, fontSize: '0.875rem' }}>/user/month</Typography>
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                  {planDetails.storage} storage per user
                </Typography>
              </Box>

              <Typography sx={{ fontWeight: 600, color: '#111827', mb: 2, fontFamily: 'Inter, sans-serif' }}>
                What's included:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {planDetails.features.map((feature, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon sx={{ color: '#22c55e', fontSize: 18 }} />
                    <Typography sx={{ color: '#374151', fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>{feature}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: 4, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckIcon sx={{ color: '#ffffff', fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                    15-day free trial
                  </Typography>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}>
                    No credit card required
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </SetupLayout>
  );
};

export default OrganizationSignup;
